import {
  AuthAccountCanadaVisaStatusChoices,
  Form,
  Modal,
  Typography,
  handleApiRequest,
} from "@cpj-ai/panel-npm-package";
import {
  ProfileCompletionMutation,
  ProfileCompletionMutationVariables,
  useProfileCompletionMutation,
} from "@graphql/generated";
import { useDisclosure } from "@heroui/react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { getInputConfig } from "./formConfig";
import useProfileCompletionData from "./useProfileCompletionData";

type ProfileCompletionModalProps = {
  onComplete?: () => void;
  isUserUploaded?: boolean;
};

const ProfileCompletionModal = ({ onComplete, isUserUploaded = false }: ProfileCompletionModalProps) => {
  const { isOpen, onClose, onOpen } = useDisclosure({ defaultOpen: isUserUploaded });
  const formHandler = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const [updating, setUpdating] = useState(false);

  const { isLoading: isLoadingProfile, fetchData, profileData } = useProfileCompletionData({ formHandler });

  useEffect(() => {
    if (isUserUploaded) {
      onOpen();
      fetchData();
    }
  }, [isUserUploaded, onOpen, fetchData]);

  const [updateProfileWithCanadaVisa] = useProfileCompletionMutation();

  const handleSubmit = useCallback(async () => {
    setUpdating(true);

    const formData = formHandler.getValues();

    await handleApiRequest<ProfileCompletionMutation, ProfileCompletionMutationVariables>({
      apiFunction: updateProfileWithCanadaVisa,
      variables: {
        firstName: formData?.firstName,
        lastName: formData?.lastName,
        gender: typeof formData.gender === "string" ? formData.gender : formData.gender.value,
        phone: formData?.phone,
        visaStatus: formData?.visaStatus?.value ?? AuthAccountCanadaVisaStatusChoices.NO_VISA,
      },
      componentName: "ProfileCompletionModal",
      actionName: "profileCompletion",
      successMessage: "Profile updated successfully",
      fallbackErrorMessage: "Failed to update profile. Please try again.",
      formHandler,
      onSuccess: () => {
        onClose();
        if (onComplete) onComplete();
      },
    });

    setUpdating(false);
  }, [formHandler, updateProfileWithCanadaVisa, onClose, onComplete]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      isDismissable={false}
      size="4xl"
      label="Complete Your Profile"
      bodyContent={
        <div className="p-2">
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-3 mb-4">
            <Typography className="text-warning-700 font-medium">
              Complete these essential details to enhance your job matching
            </Typography>
            <Typography className="text-warning-600 text-sm mt-1">
              This information is crucial for our system to accurately match you with suitable job opportunities.
            </Typography>
          </div>

          {isLoadingProfile ? (
            <div className="flex justify-center py-8">
              <Typography className="text-default-500">Loading your profile data...</Typography>
            </div>
          ) : (
            <Form
              wrapInCard={false}
              formHandler={formHandler}
              inputConfigs={getInputConfig(profileData?.profile?.country?.code2)}
              showCancel={false}
              submitProps={{
                onClick: formHandler.handleSubmit(handleSubmit),
                children: "Complete Profile",
                isLoading: updating,
                isDisabled: updating,
              }}
            />
          )}
        </div>
      }
    />
  );
};

export default ProfileCompletionModal;
